/**
 * Spoken vocabulary for loading activities onto a room stage (#2822).
 *
 * Spoken names are not moduleIds — "SociaPath" and "cards against sanity" are the
 * same activity, and nobody says "telequiz" out loud. The picker's catalog lives in
 * `packages/web-next/src/services/ModuleConfigs.ts`, which signaling cannot import,
 * so the voice vocabulary lives here where both sides can reach it.
 *
 * Deliberately NOT the full module catalog: only activities that are both
 * (a) selectable in the picker today and (b) reachable through the
 * `activate-module` socket path. `checkers` is commented out of the catalog, and
 * `brady-bunch` is a client-local DOM event with no socket path at all — neither
 * can be voice-loaded without separate work.
 */

export interface VoiceLoadableActivity {
  /** The `moduleId` the activation path expects. */
  moduleId: string;
  /** Spoken in the acknowledgement: "Starting Shout It." */
  displayName: string;
  /**
   * Lowercase spoken forms. Order here is irrelevant — resolution is
   * longest-match-wins so that "cards against sanity" beats a bare "cards".
   */
  aliases: string[];
}

/**
 * Alias lists include how speech recognizers actually RENDER these names, not
 * just how they are spelled (#2829). Panda's first live test said "start Shout
 * It" and the transcript read **"Start shouted."** — a spelling-only table
 * misses constantly, because the recognizer is transcribing English words, not
 * product names.
 */
export const VOICE_LOADABLE_ACTIVITIES: readonly VoiceLoadableActivity[] = [
  { moduleId: 'holdem', displayName: 'Texas Hold’em', aliases: ['poker', 'holdem', 'hold em', 'texas holdem', 'texas hold em', 'texas hold’em'] },
  { moduleId: 'tennis', displayName: 'Tennis', aliases: ['tennis', 'telegames tennis', 'argon tennis'] },
  {
    moduleId: 'own-it-or-bluff',
    displayName: 'Bluff Off',
    aliases: [
      'bluff off',
      'bluffoff',
      'own it or bluff',
      'own it and bluff',
      'own it',
      'bluff game',
    ],
  },
  {
    moduleId: 'pallets',
    displayName: 'Pallets',
    // Expected STT renderings: "pallets" is homophonous with palettes / pallet.
    aliases: ['pallets', 'pallet', 'palettes', 'palette', 'pallets card game'],
  },
  {
    moduleId: 'telequiz',
    displayName: 'Tele Quiz',
    aliases: [
      'tele quiz trivia challenge',
      'trivia challenge',
      'tele quiz',
      'telequiz',
      'tele-quiz',
      'trivia',
      'quiz',
    ],
  },
  {
    moduleId: 'shoutit',
    displayName: 'Shout It',
    aliases: [
      'shout it out',
      'shout it',
      'shoutit',
      // STT renderings observed / expected — "shout it" is homophonous with a
      // single English word, which is the failure mode Panda actually hit.
      'shouted',
      'shout out',
      'shouts it',
    ],
  },
  {
    moduleId: 'cards-against-sanity',
    displayName: 'SociaPath',
    aliases: [
      'cards against sanity',
      'cards against humanity',
      'sociopath',
      'socio path',
      'sociapath',
      'socia path',
      'social path',
      'soda path',
      'sosa path',
    ],
  },
  {
    moduleId: 'most-likely-to',
    displayName: 'Most Likely To',
    aliases: ['most likely to', 'most likely'],
  },
  {
    moduleId: 'mini-moto',
    displayName: 'Mini Moto',
    aliases: [
      'mini moto',
      'minimoto',
      'mini-moto',
      // Expected STT renderings of the product name.
      'mini motor',
      'many moto',
      'motocross',
      'moto cross',
      'dirt bike race',
    ],
  },
] as const;

/**
 * Longest-alias-wins resolution, so "cards against sanity" is never shadowed by
 * "cards". Borrowed from Keystone's SkillCommandRouter, which learned the same
 * lesson: a shorter alias that happens to be a prefix must not win.
 *
 * Matching is substring-based rather than grammatical — real speech produces
 * "let's play some trivia" and "can you fire up shout it", which no verb+object
 * grammar survives. The caller is responsible for having already established
 * that this is a load request.
 */
export function resolveVoiceLoadableActivity(
  text: string | null | undefined
): VoiceLoadableActivity | null {
  if (!text) return null;
  const haystack = text.toLowerCase();

  let best: VoiceLoadableActivity | null = null;
  let bestLength = 0;

  for (const activity of VOICE_LOADABLE_ACTIVITIES) {
    for (const alias of activity.aliases) {
      if (alias.length > bestLength && haystack.includes(alias)) {
        best = activity;
        bestLength = alias.length;
      }
    }
  }

  return best;
}
